import { FortuneCell } from '../types';
import { shiftFormulaRefs } from './formulaRefs';
import { recomputeDisplayValue } from './workbookInternals';

export type PasteMode = 'all' | 'values' | 'formats';

/** Everything "Formats" carries — style only, no borders/merges. */
export const STYLE_KEYS = ['bg', 'fc', 'ff', 'fs', 'bl', 'it', 'un', 'cl', 'ht', 'vt', 'tb'] as const;

/** Row/column-swapped copy of `grid` — `out[j][i] = grid[i][j]`. The
 *  `extends unknown` clause (not a bare trailing comma, which prettier
 *  strips back out) disambiguates the generic from a JSX tag under this
 *  repo's tsx-mode loader for .ts files. */
export const transpose = <T extends unknown>(grid: T[][]): T[][] => {
    if (!grid.length) return [];
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    const out: T[][] = Array.from({ length: cols }, () => new Array(rows));
    for (let i = 0; i < rows; i += 1) {
        for (let j = 0; j < cols; j += 1) out[j][i] = grid[i][j];
    }
    return out;
};

/** Projects one source cell down to what `mode` actually pastes.
 *  'all' keeps everything; 'values' keeps only v/m/ct (drops the formula AND
 *  every style key); 'formats' keeps only STYLE_KEYS + ct.fa (never v/f). */
export const projectCells =
    (mode: PasteMode) =>
    (cell: FortuneCell | null): FortuneCell | null => {
        if (!cell) return null;
        if (mode === 'all') {
            // A pasted merge marker would silently merge unrelated destination
            // cells — Excel's Paste All does not carry merge state.
            const { mc, ...rest } = cell;
            return rest;
        }

        if (mode === 'values') {
            const out: FortuneCell = {};
            if (cell.v !== undefined) out.v = cell.v;
            if (cell.m !== undefined) out.m = cell.m;
            if (cell.ct) out.ct = { ...cell.ct };
            return out;
        }

        const out: FortuneCell = {};
        STYLE_KEYS.forEach((key) => {
            const value = cell[key];
            if (value !== undefined) (out as Record<string, unknown>)[key] = value;
        });
        if (cell.ct?.fa) out.ct = { fa: cell.ct.fa };
        return out;
    };

/** Every STYLE_KEYS value the cell carries — never v/f/mc/ps/hl. Shared with
 *  `sheetActions.ts`'s Ctrl+Enter fill, whose literal branch needs the exact
 *  same "style only" projection this module already builds for Paste Special. */
export const pickStyle = (cell: FortuneCell | null | undefined): FortuneCell => {
    const out: FortuneCell = {};
    STYLE_KEYS.forEach((key) => {
        const value = cell?.[key];
        if (value !== undefined) (out as Record<string, unknown>)[key] = value;
    });
    return out;
};

/** `dest`'s own merge marker, spreadable onto a rebuilt cell — shared by
 *  every write path that only ever reaches a merged destination when it's
 *  FULLY covered (the caller refuses a partial overlap outright): this
 *  module's own Paste Special modes below, and `sheetActions.ts`'s Ctrl+Enter
 *  fill. Dropping it there would silently unmerge the cell in the grid while
 *  config.merge still claims it's merged. */
export const carryMc = (dest: FortuneCell | null | undefined): Pick<FortuneCell, 'mc'> => (dest?.mc ? { mc: { ...dest.mc } } : {});

/**
 * Merges a projected source cell INTO the destination instead of overwriting
 * it wholesale — the previous apply (`sheet.data[r][c] = cell`) erased the
 * destination's own value under Formats and its own styling under Values.
 * - 'formats': keeps the destination's v/m/f, overlays the source's styling +
 *   number format, then recomputes `m` for the destination's own value
 *   (mirrors Format Cells' own recompute).
 * - 'values': keeps the destination's styling, takes the source's v/m/ct,
 *   drops any formula.
 * - 'all': the projected source replaces the cell outright.
 * - a null projected cell (a blank source, e.g. an empty external-text token)
 *   clears v/m/f but keeps the destination's styling — Excel pastes a blank,
 *   it doesn't strip formatting.
 */
export const mergePasteCell = (dest: FortuneCell | null, projected: FortuneCell | null, mode: PasteMode): FortuneCell | null => {
    if (!projected) {
        if (mode === 'formats') return dest; // nothing to apply — a no-op
        // The destination's own merge marker survives a blank paste too —
        // Excel pastes a blank, it doesn't unmerge the cell it lands on.
        const destStyle = { ...pickStyle(dest), ...carryMc(dest) };
        return Object.keys(destStyle).length ? destStyle : null;
    }

    // A fresh copy, not `projected` itself — `projected` is also the plan
    // entry `buildPastePlan` produced; sharing that reference here would let
    // a later in-place edit of the destination cell (e.g. usePasteSpecial.ts's
    // updateCell routing) silently mutate the plan array's own entry too.
    // The destination's OWN merge marker is kept (projectCells already
    // strips any SOURCE `mc` for 'all' — see carryMc's own doc comment).
    if (mode === 'all') return { ...projected, ...carryMc(dest) };

    if (mode === 'formats') {
        // `projected` is already projectCells('formats')'s STYLE_KEYS-only view
        // of the source cell (buildPastePlan) — spread it directly rather than
        // re-filtering an already-filtered object through pickStyle.
        const next: FortuneCell = { ...dest, ...projected };
        if (projected.ct?.fa) {
            const fa = projected.ct.fa;
            next.ct = { ...dest?.ct, fa };
            if (next.v != null) {
                const m = recomputeDisplayValue(fa, next.v);
                if (m !== undefined) next.m = m;
            }
        }
        return next;
    }

    // values: keep the destination's own styling (and merge marker — same
    // reasoning as 'all' above), replace value/formula/type from the source.
    return {
        ...pickStyle(dest),
        v: projected.v,
        m: projected.m,
        ct: projected.ct ? { ...projected.ct } : undefined,
        ...carryMc(dest),
    };
};

export interface PastePlanCell {
    r: number;
    c: number;
    cell: FortuneCell | null;
}

export interface BuildPastePlanOptions {
    mode: PasteMode;
    transpose?: boolean;
    /** The copied block's own top-left cell — used to compute the uniform
     *  row/column delta every formula in the block shifts by. Omitted for a
     *  paste with no formulas to shift (e.g. an external-clipboard paste). */
    sourceOrigin?: { r: number; c: number };
}

/**
 * Projects `source` per `mode`, applies Transpose, and lays the result
 * out at `dest` — one uniform (dr, dc) formula shift for the whole block
 * (mirrors a normal Excel paste; this plan does not special-case per-cell
 * transposed formula geometry, which no acceptance case exercises).
 */
export const buildPastePlan = (
    source: (FortuneCell | null)[][],
    dest: { r: number; c: number },
    opts: BuildPastePlanOptions,
): PastePlanCell[] => {
    const grid = opts.transpose ? transpose(source) : source;
    const project = projectCells(opts.mode);
    const dr = opts.sourceOrigin ? dest.r - opts.sourceOrigin.r : 0;
    const dc = opts.sourceOrigin ? dest.c - opts.sourceOrigin.c : 0;

    const plan: PastePlanCell[] = [];
    grid.forEach((row, i) => {
        row.forEach((rawCell, j) => {
            const projected = project(rawCell);
            if (projected?.f) projected.f = shiftFormulaRefs(projected.f, dr, dc);
            plan.push({ r: dest.r + i, c: dest.c + j, cell: projected });
        });
    });
    return plan;
};
